import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const SkodaCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question:
        "Is zero depreciation add-on useful for Skoda Slavia or Kushaq?",
      answer:
        "Yes, especially in the first few years to reduce out-of-pocket costs for repair parts.",
    },
    {
      question: "Are Skoda premiums higher than Indian brands?",
      answer:
        "Often they are marginally higher due to premium components, build quality and IDV levels.",
    },
    {
      question: "Can I insure a Skoda Octavia?",
      answer:
        "Yes. Comprehensive plans are available with suitable add-on options for older or imported variants.",
    },
    {
      question: "Which Skoda model is cheapest to insure?",
      answer:
        "The Slavia tends to have the lowest premium due to lower IDV and simpler mechanics.",
    },
    {
      question: "Can I renew expired Skoda insurance online?",
      answer:
        "Yes, online renewal is available, though long lapsed policies may require vehicle inspection.",
    },
    {
      question: "Do diesel variants cost more than petrol?",
      answer:
        "Usually yes, due to higher engine valuation and repair complexity.",
    },
    {
      question: "What happens if I sell my Skoda car mid-policy?",
      answer:
        "You can transfer the policy to the new owner or cancel and claim refund for unused premium days.",
    },
    {
      question: "Do insurers cover alloy wheel or audio accessories?",
      answer:
        "Only if declared and added at the time of policy purchase. Hidden accessories may not be covered.",
    },
    {
      question: "Does comprehensive cover flood and fire damage?",
      answer:
        "Yes, under a comprehensive policy, it includes natural calamities and third-party liabilities.",
    },
    {
      question: "Is NCB transfer allowed when switching to a new Skoda car?",
      answer:
        "Yes, NCB is linked to the vehicle owner and can be transferred to a new registered car.",
    },
    {
      question: "Are tyres covered under basic Skoda insurance?",
      answer:
        "Typically only for accident-related damage. Tyre cover add-on is needed for full protection.",
    },
    {
      question: "Do insurers support cashless claims at Skoda garages?",
      answer:
        "Yes, most major insurers have tie-ups with Skoda authorized service centres.",
    },
    {
      question: "Can I add family members as drivers on my policy?",
      answer:
        "Yes, most insurers allow multiple named drivers on the same policy.",
    },
    {
      question: "How long does a Skoda insurance claim take?",
      answer:
        "Usually around 7 to 14 working days if documentation and damage assessment are in order.",
    },
    {
      question: "Does car paint colour affect Skoda insurance cost?",
      answer: "No, colour does not influence insurance premiums.",
    },
    {
      question: "Can I switch insurers mid-policy?",
      answer:
        "Typically no; switching is allowed only at renewal unless special cases apply.",
    },
    {
      question: "Is engine protection worth it for Kushaq or Octavia owners?",
      answer:
        "Highly recommended, especially if you use your car in waterlogged or rough road areas.",
    },
    {
      question: "Can I insure a 10-year-old Skoda car?",
      answer:
        "Yes, depending on the model, condition and insurer policy, both third-party and certain comprehensive plans are available.",
    },
    {
      question: "What’s the penalty for driving an uninsurable Skoda on road?",
      answer:
        "Driving without valid insurance may result in fines from ₹2,000 to ₹4,000 and possible licence suspension.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Ideal for models like Slavia, Kushaq and Octavia to avoid deductions on plastic or fibre parts.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description: "Useful in flood-prone areas or for turbo diesel variants.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description:
        "Recommended for expensive models to get full invoice value in case of total loss or theft.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Useful for long drives, flat tyres or breakdown support.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Covers parts like oils, brake fluid and nuts used during service or repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
  const skodainsurance = [
    {
      name: "Slavia",
      path: "/skoda-slavia-car-insurance",
      image: "/assets/skodaslavia.jpeg",
    },
      {
      name: "Kushaq",
      path: "/skoda-kushaq-car-insurance",
      image: "/assets/skodakushaq.jpeg",
    },
     {
      name: "Kodiaq",
      path: "/skoda-kodiaq-car-insurance",
      image: "/assets/skodakodiaq.jpeg",
    },
    {
      name: "Superb",
      path: "/skoda-superb-car-insurance",
      image: "/assets/skodasuperb.jpeg",
    },
    {
      name: "Octavia",
      path: "/skoda-octavia-car-insurance",
      image: "/assets/skodaoctavia.jpeg",
    },
     {
      name: "Rapid",
      path: "/skoda-rapid-car-insurance",
      image: "/assets/skodaorapid.jpeg",
    },  {
      name: "Fabia",
      path: "/skoda-fabia-car-insurance",
      image: "/assets/skodafabia.jpeg",
    },
     {
      name: "Laura",
      path: "/skoda-laura-car-insurance",
      image: "/assets/skodalaura.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Skoda Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Skoda car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Skoda vehicle."
        />
        <meta
          name="keywords"
          content="skoda car insurance, auto insurance india, skoda slavia insurance, skoda kushaq insurance, car insurance quotes"
        />
         <link
          rel="canonical"
          href="https://thefinmaster.com/skoda-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Skoda Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Skoda Car Insurance</span>{" "}
                
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  Skoda is known for its elegant design, solid build quality,
                  and premium driving experience. Whether you own a compact
                  Slavia or a spacious Kodiaq, keeping your car protected begins
                  with the right insurance coverage. This guide walks you
                  through everything you need to know about insuring a Skoda
                  vehicle, from average premiums and model insights to useful
                  add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/skodainsurance.jpeg"
                alt="Skoda Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Skoda in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Skoda Auto India offers a range of vehicles combining German
            engineering with Indian usability. Known for refined cabins, solid
            build and performance, the lineup includes hatchbacks, sedans and
            SUVs. Models like the Skoda Slavia, Kushaq and Octavia appeal to
            those looking for driving comfort, safety and technology-rich
            features.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Owning a Skoda means pairing it with an insurance policy that
            matches the brand’s quality and component value.
          </p>
        </div>
      </div>
      <div className="p-4 sm:p-8 bg-white">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
          Choose Your Car Brand
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {skodainsurance.map((calc, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.04 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link
                to={calc.path}
                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
              >
                <img
                  src={calc.image}
                  alt={calc.name}
                  className="w-16 h-16 object-contain mb-2"
                />
                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                  {calc.name}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Skoda Owners Need a Good Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Skoda cars are loaded with modern features including turbocharged
            engines, safety systems and premium trims. Repairing or replacing
            parts can be costly. A comprehensive insurance policy can protect
            you from:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Road accidents and collision damage</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft, fire and vandalism</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Natural disasters such as floods or storms</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party liability claims</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>
                High charges for European build and electronic components
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Your policy also grants access to Skoda-authorized garages, cashless
            repairs and flexible add-on options for your driving conditions.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Skoda Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s an overview of Skoda’s most popular models in India, along with
          estimated Insured Declared Value (IDV) and starting premium range for
          comprehensive plans:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Skoda Slavia",
                category: "Mid-Size Sedan",
                idv: "9–13 Lakh",
                premium: "₹4,000 to ₹6,500",
              },
              {
                model: "Skoda Kushaq",
                category: "Compact SUV",
                idv: "12–18 Lakh",
                premium: "₹4,500 to ₹7,800",
              },
              {
                model: "Skoda Octavia",
                category: "Premium Sedan",
                idv: "18–25 Lakh",
                premium: "₹5,500 to ₹8,900",
              },
              {
                model: "Skoda Superb",
                category: "Executive Sedan",
                idv: "25–32 Lakh",
                premium: "₹6,800 to ₹10,500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Skoda Slavia",
                    "Mid-Size Sedan",
                    "9–13 Lakh",
                    "₹4,000 to ₹6,500",
                  ],
                  [
                    "Skoda Kushaq",
                    "Compact SUV",
                    "12–18 Lakh",
                    "₹4,500 to ₹7,800",
                  ],
                  [
                    "Skoda Octavia",
                    "Premium Sedan",
                    "18–25 Lakh",
                    "₹5,500 to ₹8,900",
                  ],
                  [
                    "Skoda Superb",
                    "Executive Sedan",
                    "25–32 Lakh",
                    "₹6,800 to ₹10,500",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Skoda Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          To minimize costly repair bills and upkeep, Skoda drivers should
          consider:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Skoda Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          When choosing an insurer for a Skoda vehicle, these providers are
          often preferred for their service records, high claim settlement ratio
          and garage network:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Reliable for premium European models.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers smooth digital onboarding and claim support.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Flexible plans with strong add-on options.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Affordable pricing and quick policy issuance.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Efficient customer service and strong garage reach.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Skoda Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Here are a few practical measures to reduce your premium while
              retaining effective coverage:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare insurance quotes before finalising.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Keep your claim history clean to maintain No Claim Bonus
                  (NCB).
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Choose a higher voluntary deductible if you are a safe driver.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install certified anti-theft equipment for discount points.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew your policy on or before its expiry to avoid penalties.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Skoda Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew insurance for your Skoda vehicle, you typically
              need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Aadhaar or PAN for verification</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-size photo (offline purchase only)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Skoda Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Skoda vehicles are crafted for driving pleasure, comfort and
                style. A tailored insurance plan can shield you from financial
                surprises whether you drive in city traffic or on highways.
                Whether it’s a Slavia or an Octavia, a well-chosen comprehensive
                policy with the right add-ons makes ownership more secure.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you find the best premiums, choose
                relevant add-ons and compare plans to secure your Skoda vehicle
                effectively.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default SkodaCarInsurance;
